import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const galleryItems = [
  {
    id: 1,
    category: 'Before & After',
    title: 'Garden Clearance – Dublin',
    before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
    after: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=80',
    type: 'before-after',
  },
  {
    id: 2,
    category: 'House Clearance',
    title: 'Full House Clear-Out',
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80',
    type: 'single',
  },
  {
    id: 3,
    category: 'Rubbish Removal',
    title: 'General Waste Removal',
    image: 'https://images.unsplash.com/photo-1605600659908-0ef719419d41?w=600&q=80',
    type: 'single',
  },
  {
    id: 4,
    category: 'Furniture Removal',
    title: 'Old Furniture Disposal',
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80',
    type: 'single',
  },
  {
    id: 5,
    category: 'Before & After',
    title: 'Driveway Clearance – Meath',
    before: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
    after: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
    type: 'before-after',
  },
  {
    id: 6,
    category: 'Commercial',
    title: 'Office Clear-Out',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80',
    type: 'single',
  },
  {
    id: 7,
    category: 'Garden Waste',
    title: 'Garden & Green Waste',
    image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=80',
    type: 'single',
  },
  {
    id: 8,
    category: 'Appliance Removal',
    title: 'White Goods Collection',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80',
    type: 'single',
  },
  {
    id: 9,
    category: 'Estate Clearance',
    title: 'Full Estate Clear-Out',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
    type: 'single',
  },
];

const categories = ['All', 'Before & After', 'House Clearance', 'Rubbish Removal', 'Furniture Removal', 'Commercial', 'Garden Waste', 'Appliance Removal', 'Estate Clearance'];

export default function Gallery() {
  useScrollAnimation();
  const [filter, setFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const [beforeAfterView, setBeforeAfterView] = useState({});

  const filtered = filter === 'All' ? galleryItems : galleryItems.filter((g) => g.category === filter);

  const openLightbox = (item, imageUrl) => {
    setLightbox({ item, imageUrl });
  };

  const closeLightbox = () => setLightbox(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div>
      {/* PAGE HEADER */}
      <section className="hero-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-green-400 font-bold text-sm uppercase tracking-widest">Our Work</span>
          <h1 className="text-5xl font-black text-white mt-3 mb-4">Gallery</h1>
          <p className="text-green-200 text-lg max-w-2xl mx-auto">
            See the results for yourself. From single item removals to full property clearances — here's a glimpse of the work we do every day.
          </p>
        </div>
      </section>

      {/* FILTER */}
      <section className="py-10 bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 flex-wrap justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  filter === cat
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY GRID */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <div key={item.id} className="animate-on-scroll">
                {item.type === 'before-after' ? (
                  <div className="bg-white rounded-2xl overflow-hidden shadow-md card-hover">
                    <div className="relative">
                      <div className="grid grid-cols-2 gap-0">
                        <div className="relative overflow-hidden">
                          <img
                            src={item.before}
                            alt={`${item.title} - Before`}
                            className="w-full h-48 object-cover transition-transform duration-400"
                          />
                          <div className="absolute bottom-2 left-2 bg-red-500 text-white text-xs font-black px-2 py-1 rounded-lg">
                            BEFORE
                          </div>
                        </div>
                        <div className="relative overflow-hidden">
                          <img
                            src={item.after}
                            alt={`${item.title} - After`}
                            className="w-full h-48 object-cover transition-transform duration-400"
                          />
                          <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs font-black px-2 py-1 rounded-lg">
                            AFTER
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <span className="text-xs font-bold text-green-600 uppercase tracking-wider">{item.category}</span>
                      <h3 className="font-bold text-gray-900 mt-1">{item.title}</h3>
                    </div>
                  </div>
                ) : (
                  <div
                    className="bg-white rounded-2xl overflow-hidden shadow-md card-hover cursor-pointer group"
                    onClick={() => openLightbox(item, item.image)}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                        <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                    <div className="p-4">
                      <span className="text-xs font-bold text-green-600 uppercase tracking-wider">{item.category}</span>
                      <h3 className="font-bold text-gray-900 mt-1">{item.title}</h3>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              <p className="text-lg">No items in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* BEFORE / AFTER HIGHLIGHT */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Results Speak</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">The Lawlor Transformation</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We serve Dublin, Meath, Louth and Kildare. Here's what a difference we make.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Garden Clearance', time: '2 Hours', location: 'Dublin', saving: '€280 Quote' },
              { title: 'House Clear-Out', time: 'Same Day', location: 'Meath', saving: '€450 Quote' },
              { title: 'Commercial Waste', time: '3 Hours', location: 'Louth', saving: '€350 Quote' },
            ].map(({ title, time, location, saving }) => (
              <div key={title} className="bg-gray-50 rounded-2xl p-6 text-center animate-on-scroll">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-black text-lg">✓</span>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{title}</h3>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="bg-white rounded-lg p-2">
                    <div className="text-green-600 font-bold text-sm">{time}</div>
                    <div className="text-gray-400 text-xs">Duration</div>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <div className="text-green-600 font-bold text-sm">{location}</div>
                    <div className="text-gray-400 text-xs">County</div>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <div className="text-green-600 font-bold text-sm">{saving}</div>
                    <div className="text-gray-400 text-xs">Fair Price</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hero-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black text-white mb-4">Want Results Like These?</h2>
          <p className="text-green-200 text-lg mb-8">Call Chris today for a free quote. Same day service available across Dublin, Meath, Louth & Kildare.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0852730131"
              className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-black px-10 py-5 rounded-2xl transition-all text-xl"
            >
              <Phone size={24} />
              085 273 0131
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold px-10 py-5 rounded-2xl transition-all text-lg"
            >
              Get a Free Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
            onClick={closeLightbox}
          >
            <X size={28} />
          </button>
          <div className="max-w-4xl max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.imageUrl}
              alt={lightbox.item.title}
              className="max-w-full max-h-[80vh] object-contain rounded-xl"
            />
            <div className="text-center mt-4">
              <span className="text-green-400 text-sm font-bold uppercase tracking-wider">{lightbox.item.category}</span>
              <p className="text-white font-bold text-lg">{lightbox.item.title}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
